import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Download, Filter, Users, Calendar, MessageCircle, Mail, Loader2, ArrowRightLeft, Pencil, UserPlus, Search } from "lucide-react";

const API = `${window.location.origin}/api`;
const BASE_URL = "https://odr-academy.emergent.host";

const AdminBookingsPage = () => {
  const [sendingEmail, setSendingEmail] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newCourseId, setNewCourseId] = useState("");
  const [moving, setMoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [users, setUsers] = useState([]);
  const [referenti, setReferenti] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedEnrollCourseId, setSelectedEnrollCourseId] = useState("");
  const [sendEnrollEmail, setSendEnrollEmail] = useState(true);
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    centro_estetico: "",
    indirizzo_centro: "",
    referente_id: ""
  });
  const [editForm, setEditForm] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    centro_estetico: "",
    indirizzo_centro: "",
    new_course_id: ""  // Per cambiare sessione/data
  });
  
  // Function to generate WhatsApp share link
  const generateWhatsAppMessage = (booking) => {
    const courseDate = new Date(booking.course_date).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
    
    // Check if it's February 2nd session (automatic enrollment)
    const isAutoEnrollment = booking.course_date === "2026-02-02";
    
    let message = `✨ *ODR Academy - La Bellezza che Cura* ✨\n\n`;
    
    message += `Ciao ${booking.user_name}!\n`;
    
    if (isAutoEnrollment) {
      message += `Sei stata iscritta automaticamente alla sessione formativa.\n`;
      message += `*Non è necessaria alcuna registrazione!*\n\n`;
    } else {
      message += `La tua iscrizione è confermata.\n\n`;
    }
    
    message += `━━━━━━━━━━━━━━━━━━\n`;
    message += `📚 *${booking.course_title}*\n`;
    message += `━━━━━━━━━━━━━━━━━━\n\n`;
    message += `📅 *Data:* ${courseDate}\n`;
    message += `🕐 *Ora:* ${booking.course_time}\n`;
    message += `👩‍🏫 *Docente:* ${booking.instructor}\n\n`;
    
    if (booking.zoom_link) {
      message += `━━━━━━━━━━━━━━━━━━\n`;
      message += `🎥 *LINK VIDEOCONFERENZA*\n`;
      message += `Clicca per partecipare:\n`;
      message += `${booking.zoom_link}\n`;
      if (booking.zoom_password) {
        message += `🔑 Password: ${booking.zoom_password}\n`;
      }
      message += `━━━━━━━━━━━━━━━━━━\n\n`;
    }
    
    // Direct link to test (no password needed)
    message += `📝 *TEST DI VALUTAZIONE*\n`;
    message += `Accesso diretto (nessuna password):\n`;
    message += `${BASE_URL}/quiz\n\n`;
    
    message += `A presto! 💕\n`;
    message += `_ODR Academy - La Bellezza che Cura_`;
    
    return encodeURIComponent(message);
  };

  const handleWhatsAppShare = (booking) => {
    console.log("WhatsApp clicked for:", booking.user_name);
    // Clean phone number (remove spaces, dashes, etc.)
    let phone = booking.user_phone ? booking.user_phone.replace(/[\s\-\(\)\.]/g, "") : "";
    
    console.log("Phone:", phone);
    
    // Add Italy country code if not present
    if (phone && !phone.startsWith("+")) {
      if (phone.startsWith("00")) {
        phone = "+" + phone.substring(2);
      } else if (phone.startsWith("0")) {
        phone = "+39" + phone.substring(1);
      } else {
        phone = "+39" + phone;
      }
    }
    
    const message = generateWhatsAppMessage(booking);
    const whatsappUrl = `https://wa.me/${phone.replace("+", "")}?text=${message}`;
    
    console.log("WhatsApp URL:", whatsappUrl);
    window.open(whatsappUrl, "_blank");
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => u.role === "cliente"));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchReferenti = async () => {
    try {
      const response = await axios.get(`${API}/referenti`);
      setReferenti(response.data);
    } catch (error) {
      console.error("Error fetching referenti:", error);
    }
  };

  const openEnrollDialog = () => {
    fetchUsers();
    fetchReferenti();
    setSelectedUserId("");
    setSelectedEnrollCourseId("");
    setSendEnrollEmail(true);
    setUserSearch("");
    setEnrollDialogOpen(true);
  };

  const openCreateUserDialog = () => {
    fetchReferenti();
    setNewUserForm({
      name: "",
      email: "",
      password: "",
      phone: "",
      centro_estetico: "",
      indirizzo_centro: "",
      referente_id: ""
    });
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error("Compila nome, email e password");
      return;
    }

    setCreatingUser(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/users/create`,
        newUserForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Cliente "${newUserForm.name}" creato con successo!`);
      setCreateUserDialogOpen(false);
      
      // Refresh users list and select the new user
      await fetchUsers();
      setSelectedUserId(response.data.user_id);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleEnrollUser = async () => {
    if (!selectedUserId || !selectedEnrollCourseId) {
      toast.error("Seleziona un cliente e una sessione");
      return;
    }

    setEnrolling(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/bookings/create`,
        {
          user_id: selectedUserId,
          course_id: selectedEnrollCourseId,
          send_email: sendEnrollEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const user = users.find(u => u.user_id === selectedUserId);
      const course = courses.find(c => c.course_id === selectedEnrollCourseId);
      toast.success(`${user?.name} iscritta a "${course?.title}"`);
      setEnrollDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'iscrizione");
    } finally {
      setEnrolling(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.centro_estetico?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSendEmail = async (booking) => {
    setSendingEmail(booking.booking_id);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/bookings/${booking.booking_id}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email inviata a ${booking.user_email}`);
    } catch (error) {
      toast.error("Errore nell'invio dell'email");
    } finally {
      setSendingEmail(null);
    }
  };

  const openMoveDialog = (booking) => {
    setSelectedBooking(booking);
    setNewCourseId("");
    setMoveDialogOpen(true);
  };

  const handleMoveBooking = async () => {
    if (!selectedBooking || !newCourseId) {
      toast.error("Seleziona una sessione di destinazione");
      return;
    }

    if (newCourseId === selectedBooking.course_id) {
      toast.error("Seleziona una sessione diversa");
      return;
    }

    setMoving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/bookings/${selectedBooking.booking_id}/move`,
        { new_course_id: newCourseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newCourse = courses.find(c => c.course_id === newCourseId);
      toast.success(`${selectedBooking.user_name} spostata a "${newCourse?.title || 'nuova sessione'}"`);
      setMoveDialogOpen(false);
      fetchData(); // Ricarica i dati
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nello spostamento");
    } finally {
      setMoving(false);
    }
  };

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setEditForm({
      user_name: booking.user_name || "",
      user_email: booking.user_email || "",
      user_phone: booking.user_phone || "",
      centro_estetico: booking.centro_estetico || "",
      indirizzo_centro: booking.indirizzo_centro || "",
      new_course_id: booking.course_id || ""  // Sessione attuale
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBooking) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      // Se la sessione è cambiata, aggiorna anche quella
      if (editForm.new_course_id && editForm.new_course_id !== selectedBooking.course_id) {
        // Prima sposta la sessione
        await axios.put(
          `${API}/admin/bookings/${selectedBooking.booking_id}/move`,
          { new_course_id: editForm.new_course_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Poi aggiorna i dati cliente
      const clientData = {
        user_name: editForm.user_name,
        user_email: editForm.user_email,
        user_phone: editForm.user_phone,
        centro_estetico: editForm.centro_estetico,
        indirizzo_centro: editForm.indirizzo_centro
      };
      
      await axios.put(
        `${API}/admin/bookings/${selectedBooking.booking_id}/edit`,
        clientData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Dati aggiornati con successo");
      setEditDialogOpen(false);
      fetchData(); // Ricarica i dati
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const [bookings, setBookings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [referentiData, setReferentiData] = useState({}); // Mappa referente_id -> {colore, ruolo}
  const [loading, setLoading] = useState(true);
  const [courseFilter, setCourseFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, sessionsRes, referentiRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/sessions`),
        axios.get(`${API}/referenti`),
      ]);
      setBookings(bookingsRes.data);
      // Map sessions to courses format for compatibility
      const mappedSessions = sessionsRes.data.map(s => ({
        course_id: s.session_id,
        title: s.title,
        date: s.date,
        time: s.time,
        instructor: s.docente_name,
        max_participants: s.max_participants,
        current_participants: s.current_participants
      }));
      setCourses(mappedSessions);
      
      // Crea mappa referente_id -> {colore, ruolo}
      const dataMap = {};
      referentiRes.data.forEach(ref => {
        dataMap[ref.referente_id] = {
          colore: ref.colore || null,
          ruolo: ref.ruolo
        };
      });
      setReferentiData(dataMap);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  // Colora la riga SOLO se il referente è un DISTRIBUTORE
  const getBookingColor = (booking) => {
    if (booking.referente_id && referentiData[booking.referente_id]) {
      const refData = referentiData[booking.referente_id];
      // Applica il colore SOLO per i DISTRIBUTORI
      if (refData.ruolo === "DISTRIBUTORE" && refData.colore) {
        return refData.colore;
      }
    }
    return 'transparent';
  };

  const handleExportCSV = async () => {
    try {
      const url = courseFilter === "all"
        ? `${API}/admin/bookings/export`
        : `${API}/admin/bookings/export?course_id=${courseFilter}`;
      
      const response = await axios.get(url, { responseType: "blob" });
      
      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "iscrizioni.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Export completato");
    } catch (error) {
      toast.error("Errore durante l'export");
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, { responseType: "blob" });
      
      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "utenti.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Export utenti completato");
    } catch (error) {
      toast.error("Errore durante l'export utenti");
    }
  };

  const filteredBookings = courseFilter === "all"
    ? bookings
    : bookings.filter((b) => b.course_id === courseFilter);

  const confirmedBookings = filteredBookings.filter((b) => b.status === "confirmed");

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Gestione Iscrizioni
            </h1>
            <p className="text-muted-foreground">
              Visualizza e gestisci le iscrizioni ai corsi
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              onClick={openEnrollDialog}
              className="rounded-full bg-green-600 hover:bg-green-700"
              data-testid="enroll-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Iscrivi Cliente
            </Button>
            <Button
              onClick={handleExportUsers}
              variant="outline"
              className="rounded-full"
              data-testid="export-users-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Utenti
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="rounded-full"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Iscrizioni
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="rounded-2xl border-border/50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Iscrizioni Confermate</p>
                <p className="text-2xl font-semibold">{confirmedBookings.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Corsi Attivi</p>
                <p className="text-2xl font-semibold">{courses.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Filter className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Filtro Attivo</p>
                <p className="text-lg font-medium truncate">
                  {courseFilter === "all" ? "Tutti i corsi" : courses.find((c) => c.course_id === courseFilter)?.title || "—"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="rounded-2xl border-border/50 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Filter className="w-4 h-4" />
                Filtra per corso:
              </div>
              <Select value={courseFilter} onValueChange={setCourseFilter}>
                <SelectTrigger className="w-64 rounded-full" data-testid="course-filter">
                  <SelectValue placeholder="Seleziona corso" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i corsi</SelectItem>
                  {courses.map((course) => (
                    <SelectItem key={course.course_id} value={course.course_id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="h-64 bg-secondary rounded-2xl animate-pulse" />
        ) : confirmedBookings.length > 0 ? (
          <Card className="rounded-2xl border-border/50 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">#</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefono</TableHead>
                    <TableHead>Centro</TableHead>
                    <TableHead>Codice Referente</TableHead>
                    <TableHead>Corso</TableHead>
                    <TableHead>Data Corso</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {confirmedBookings.map((booking, index) => {
                    // Determine if parrucchiere or estetista based on course title or centro name
                    const isParrucchiere = 
                      booking.course_title?.toLowerCase().includes('parrucchier') ||
                      booking.centro_estetico?.toLowerCase().includes('parrucchier') ||
                      booking.centro_estetico?.toLowerCase().includes('hair') ||
                      booking.centro_estetico?.toLowerCase().includes('capell');
                    
                    const rowBgClass = isParrucchiere 
                      ? 'bg-blue-50 hover:bg-blue-100' 
                      : 'bg-pink-50/50 hover:bg-pink-100/50';
                    
                    return (
                    <TableRow 
                      key={booking.booking_id} 
                      data-testid={`booking-row-${booking.booking_id}`}
                      className={rowBgClass}
                      style={{ backgroundColor: getBookingColor(booking) || undefined }}
                    >
                      <TableCell className="text-center font-bold text-muted-foreground">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{booking.user_name}</span>
                          {isParrucchiere && (
                            <Badge className="bg-blue-500 text-white text-xs">Parrucchiere</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{booking.user_email}</TableCell>
                      <TableCell>{booking.user_phone}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{booking.centro_estetico}</p>
                          <p className="text-xs text-muted-foreground">{booking.indirizzo_centro}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs bg-white">
                          {booking.referente_id || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{booking.course_title}</TableCell>
                      <TableCell>
                        {new Date(booking.course_date).toLocaleDateString("it-IT")} - {booking.course_time}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={booking.status === "confirmed" ? "default" : "secondary"}
                          className="rounded-full"
                        >
                          {booking.status === "confirmed" ? "Confermato" : "Cancellato"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-green-50 hover:bg-green-100 border-green-300 text-green-700"
                            onClick={() => handleWhatsAppShare(booking)}
                            data-testid={`whatsapp-btn-${booking.booking_id}`}
                            title="Invia dettagli via WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-700"
                            onClick={() => handleSendEmail(booking)}
                            disabled={sendingEmail === booking.booking_id}
                            data-testid={`email-btn-${booking.booking_id}`}
                            title="Invia email con dettagli"
                          >
                            {sendingEmail === booking.booking_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-700"
                            onClick={() => openMoveDialog(booking)}
                            data-testid={`move-btn-${booking.booking_id}`}
                            title="Sposta a un'altra sessione"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700"
                            onClick={() => openEditDialog(booking)}
                            data-testid={`edit-btn-${booking.booking_id}`}
                            title="Modifica dati cliente"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">Nessuna iscrizione trovata</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog Iscrivi Cliente */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Iscrivi Cliente a Sessione
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Ricerca Cliente */}
            <div className="space-y-2">
              <Label>Cerca Cliente</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Nome, email o centro estetico..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 rounded-xl"
                />
              </div>
            </div>

            {/* Lista Clienti */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Seleziona Cliente *</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={openCreateUserDialog}
                  className="text-xs h-7 rounded-full"
                >
                  <UserPlus className="w-3 h-3 mr-1" />
                  Nuovo Cliente
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-xl">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => setSelectedUserId(user.user_id)}
                      className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 transition-colors ${
                        selectedUserId === user.user_id ? "bg-green-50 border-green-200" : ""
                      }`}
                    >
                      <div className="font-medium text-sm">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      {user.centro_estetico && (
                        <div className="text-xs text-gray-400">{user.centro_estetico}</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {userSearch ? "Nessun cliente trovato" : "Caricamento clienti..."}
                  </div>
                )}
              </div>
            </div>

            {/* Selezione Sessione */}
            <div className="space-y-2">
              <Label>Seleziona Sessione *</Label>
              <Select value={selectedEnrollCourseId} onValueChange={setSelectedEnrollCourseId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Scegli la sessione" />
                </SelectTrigger>
                <SelectContent>
                  {courses
                    .filter(c => new Date(c.date) >= new Date())
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((course) => (
                      <SelectItem key={course.course_id} value={course.course_id}>
                        {course.title} - {new Date(course.date).toLocaleDateString("it-IT")}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Opzione Email */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEnrollEmail"
                checked={sendEnrollEmail}
                onChange={(e) => setSendEnrollEmail(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="sendEnrollEmail" className="text-sm cursor-pointer">
                Invia email di conferma al cliente
              </Label>
            </div>

            {/* Riepilogo */}
            {selectedUserId && selectedEnrollCourseId && (
              <div className="bg-green-50 p-3 rounded-xl text-sm">
                <p className="font-medium text-green-800">Riepilogo:</p>
                <p className="text-green-700">
                  {users.find(u => u.user_id === selectedUserId)?.name} sarà iscritta a "{courses.find(c => c.course_id === selectedEnrollCourseId)?.title}"
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setEnrollDialogOpen(false)}
              className="flex-1 rounded-full"
            >
              Annulla
            </Button>
            <Button
              onClick={handleEnrollUser}
              disabled={!selectedUserId || !selectedEnrollCourseId || enrolling}
              className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
            >
              {enrolling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iscrizione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Iscrivi Cliente
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea Nuovo Cliente */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-500" />
              Registra Nuovo Cliente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Nome e Cognome *</Label>
                <Input
                  placeholder="es. Maria Rossi"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="email@esempio.it"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="text"
                  placeholder="Password iniziale"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  type="tel"
                  placeholder="+39 333 1234567"
                  value={newUserForm.phone}
                  onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Centro Estetico</Label>
                <Input
                  placeholder="Nome del centro"
                  value={newUserForm.centro_estetico}
                  onChange={(e) => setNewUserForm({...newUserForm, centro_estetico: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Indirizzo Centro</Label>
                <Input
                  placeholder="Via, Città, CAP"
                  value={newUserForm.indirizzo_centro}
                  onChange={(e) => setNewUserForm({...newUserForm, indirizzo_centro: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Referente / Distributore</Label>
                <Select 
                  value={newUserForm.referente_id || "none"} 
                  onValueChange={(value) => setNewUserForm({...newUserForm, referente_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleziona referente (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun referente</SelectItem>
                    {referenti.map((ref) => (
                      <SelectItem key={ref.referente_id} value={ref.referente_id}>
                        {ref.nome} {ref.ragione_sociale ? `(${ref.ragione_sociale})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCreateUserDialogOpen(false)}
              className="flex-1 rounded-full"
            >
              Annulla
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={!newUserForm.name || !newUserForm.email || !newUserForm.password || creatingUser}
              className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700"
            >
              {creatingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crea Cliente
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Sposta Cliente */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-500" />
              Sposta Cliente
            </DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-gray-800">{selectedBooking.user_name}</p>
                <p className="text-sm text-gray-500">{selectedBooking.user_email}</p>
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-gray-500">Sessione attuale:</p>
                  <p className="text-sm font-medium text-amber-600">{selectedBooking.course_title}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(selectedBooking.course_date).toLocaleDateString("it-IT")} - {selectedBooking.course_time}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Sposta a:</label>
                <Select value={newCourseId} onValueChange={setNewCourseId}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleziona nuova sessione" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses
                      .filter(c => c.course_id !== selectedBooking.course_id)
                      .map((course) => (
                        <SelectItem key={course.course_id} value={course.course_id}>
                          <div>
                            <p className="font-medium">{course.title}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(course.date).toLocaleDateString("it-IT")} - {course.time}
                            </p>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setMoveDialogOpen(false)}
                  className="flex-1 rounded-full"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleMoveBooking}
                  disabled={moving || !newCourseId}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 rounded-full"
                >
                  {moving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Spostamento...
                    </>
                  ) : (
                    "Sposta"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Cliente */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-500" />
              Modifica Dati Cliente
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nome e Cognome</Label>
              <Input
                id="edit-name"
                value={editForm.user_name}
                onChange={(e) => setEditForm({...editForm, user_name: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.user_email}
                onChange={(e) => setEditForm({...editForm, user_email: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefono</Label>
              <Input
                id="edit-phone"
                value={editForm.user_phone}
                onChange={(e) => setEditForm({...editForm, user_phone: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-centro">Centro Estetico</Label>
              <Input
                id="edit-centro"
                value={editForm.centro_estetico}
                onChange={(e) => setEditForm({...editForm, centro_estetico: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-indirizzo">Indirizzo</Label>
              <Input
                id="edit-indirizzo"
                value={editForm.indirizzo_centro}
                onChange={(e) => setEditForm({...editForm, indirizzo_centro: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label>Sessione / Data</Label>
              <Select 
                value={editForm.new_course_id} 
                onValueChange={(value) => setEditForm({...editForm, new_course_id: value})}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona sessione" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.course_id} value={course.course_id}>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {new Date(course.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} ore {course.time}
                        </span>
                        <span className="text-xs text-gray-500">{course.title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editForm.new_course_id !== selectedBooking?.course_id && editForm.new_course_id && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Cambiando sessione, il link Zoom sarà aggiornato automaticamente
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 bg-purple-500 hover:bg-purple-600 rounded-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  "Salva"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminBookingsPage;
// WhatsApp fix - 1769877530
